// Stats.js
import React, { useState, useEffect } from 'react';
import PerformerCard from './Performercard';
import StatsHeader from './StatsHead';
import BattingStats from './BattingStats';
import TeamStats from './TeamStats';
import PlayerStats from './PlayerStats';
import './App.css';
import BowlingStats from './BowlingStats';

function Stats({ competitionId, statsFeed }) {
    const [statsData, setStatsData] = useState({
        battingKPI: null,
        battingStats: null,
        bowlingKPI: null
    });
    const [performers, setPerformers] = useState({
        maxRuns: [],
        strikeRate: [],
        economy: [],
        wickets: []
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState("batting");
    const [activeMenu, setActiveMenu] = useState('tournament');
    const feedURL = process.env.REACT_APP_FEED_URL;
    const imageURL = process.env.REACT_APP_IMAGE_URL;
    const defaultPlayerImage = `${imageURL}player-thumb.jpg`;
    const defaultTeamImage = `${imageURL}default-team-logo.png`;
    const handleImageError = (e) => {
        e.target.src = defaultPlayerImage;
    };
    const handleTeamImageError = (e) => {
        e.target.src = defaultTeamImage;
    };

    // useEffect(() => {
    //     const fetchStats = async () => {
    //         if (!competitionId) return;
    //         const defaultFeed = `${feedURL}`;

    //         try {
    //             setLoading(true);
    //             const response = await fetch(`${statsFeed || defaultFeed}/stats/${competitionId}-tournamentmasterkpi.js`);
    //             let text = await response.text();
    //             text = text.replace(/\n/g, '').replace(/\r/g, '').trim();
    //             // Extract the JSON data from the JSONP response
    //             const jsonMatch = text.match(/onTournamentstats\((.*?)\)/);
    //             if (!jsonMatch) throw new Error('Invalid data format');

    //             const data = JSON.parse(jsonMatch[1]);
    //             setStatsData({
    //                 battingKPI: data.TournamentBattingKPI?.[0] || null,
    //                 battingStats: data.TournamentBattingStats?.[0] || null,
    //                 bowlingKPI: data.TournamentBowlingKPI?.[0] || null
    //             });
    //         } catch (err) {
    //             console.error('Error fetching stats:', err);
    //             setError(err.message);
    //         } finally {
    //             setLoading(false);
    //         }
    //     };

    //     fetchStats();
    // }, [competitionId, statsFeed]);

    useEffect(() => {
        const fetchStats = async () => {
            if (!competitionId) return;
            const defaultFeed = `${feedURL}`;

            try {
                const timestamp = new Date().getTime();
                setLoading(true);
                const response = await fetch(`${statsFeed || defaultFeed}/stats/${competitionId}-tournamentmasterkpi.js?t=${timestamp}`);
                // Handle 404 error specifically
                if (!response.ok) {
                    if (response.status === 404) {

                        setError(<div className='noRecord'>No Stats Found..</div>);
                    } else {
                        setError('No stats found');
                    }
                    setStatsData({
                        battingKPI: null,
                        battingStats: null,
                        bowlingKPI: null
                    });
                    return;
                }
                let text = await response.text();

                // Clean and sanitize the response text
                text = text.replace(/\n/g, '').replace(/\r/g, '').trim();

                // Extract JSON content using a regex
                const jsonMatch = text.match(/onTournamentstats\((.*)\)/);
                if (!jsonMatch || !jsonMatch[1]) {
                    setError('Stats not found');
                    setStatsData({
                        battingKPI: null,
                        battingStats: null,
                        bowlingKPI: null
                    });
                    return;
                }

                // Parse JSON safely
                let data;
                try {
                    data = JSON.parse(jsonMatch[1].trim());
                } catch (parseError) {
                    setError('Stats not found');
                    setStatsData({
                        battingKPI: null,
                        battingStats: null,
                        bowlingKPI: null
                    });
                    return;
                }
                // Check if data contains valid stats
                if (!data?.TournamentBattingKPI?.[0] && !data?.TournamentBattingStats?.[0] && !data?.TournamentBowlingKPI?.[0]) {
                    setError('Stats not found');
                } else {
                    setError(null);
                }
                // Set stats data
                setStatsData({
                    battingKPI: data?.TournamentBattingKPI?.[0] || null,
                    battingStats: data?.TournamentBattingStats?.[0] || null,
                    bowlingKPI: data?.TournamentBowlingKPI?.[0] || null
                });
            } catch (err) {
                setError('Stats not found');
                setStatsData({
                    battingKPI: null,
                    battingStats: null,
                    bowlingKPI: null
                });
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, [competitionId, statsFeed]);


    useEffect(() => {
        const fetchPerformers = async () => {
            if (!competitionId) return;
            const defaultFeed = `${feedURL}`;
            try {
                const timestamp = new Date().getTime();
                const response = await fetch(`${statsFeed || defaultFeed}/stats/${competitionId}-tournamentkeyperformer.js?callback=onKeyPerformer&=${timestamp}`);
                let text = await response.text();

                // Clean the response text
                text = text.replace(/\n/g, '').replace(/\r/g, '');
                const jsonMatch = text.match(/onKeyPerformer\((.*)\)/);

                if (!jsonMatch || !jsonMatch[1]) {
                    throw new Error('Invalid performers data format');
                }

                let data;
                try {
                    data = JSON.parse(jsonMatch[1].trim());
                } catch (parseError) {
                    console.error('JSON Parse Error:', parseError);
                    return;
                }

                // Set default empty arrays if data properties don't exist
                const battingPerformers = data.TournamentKeyBattingPerformers || [];
                const bowlingPerformers = data.TournamentKeyBowlingPerformers || [];

                setPerformers({
                    maxRuns: battingPerformers.filter(p => p.KPIs === "Maximum Runs")
                        .sort((a, b) => Number(b.KPIValues) - Number(a.KPIValues)),
                    strikeRate: battingPerformers.filter(p => p.KPIs === "Maximum Strike Rate")
                        .sort((a, b) => Number(b.KPIValues) - Number(a.KPIValues)),
                    economy: bowlingPerformers.filter(p => p.KPIs === "EconomyRate")
                        .sort((a, b) => Number(a.KPIValues) - Number(b.KPIValues)),
                    wickets: bowlingPerformers.filter(p => p.KPIs === "HighestWicket")
                        .sort((a, b) => Number(b.KPIValues) - Number(a.KPIValues))
                });

            } catch (err) {
                console.error('Error fetching performers:', err);
                setError(<div className="noRecord">No Stats Found</div>);
            }
        };

        fetchPerformers();
    }, [competitionId, statsFeed]);


    if (loading) return <div>Loading stats...</div>;
    if (error) return <div> {error}</div>;
    if (!statsData.battingKPI && !statsData.battingStats && !statsData.bowlingKPI) {

        return <div className="text-gray-400 text-xl font-semibold ">No stats available</div>;
    }
    const handleError = (errorMessage) => {
        setError(errorMessage);
    };
    const handleTabClick = (tab) => {
        setActiveTab(tab);
    };
    const handleMenuClick = (menu) => {
        setActiveMenu(menu);
        setActiveTab('batting');
    };
    return (
        <>
            <div className="mcContainer" >
                <div className="mcSecondaryMenu">
                    <ul className="statsSubMenu">
                        <li
                            className={activeMenu === 'tournament' ? 'current' : ''}
                            onClick={() => handleMenuClick('tournament')}
                        >
                            Tournament
                        </li>
                        <li
                            className={activeMenu === 'team' ? 'current' : ''}
                            onClick={() => handleMenuClick('team')}
                        >
                            Team
                        </li>
                        <li
                            className={activeMenu === 'player' ? 'current' : ''}
                            onClick={() => handleMenuClick('player')}
                        >
                            Player
                        </li>
                    </ul>
                </div>
            </div>
            {activeMenu === 'tournament' && (
                <div id="tournamentStatsWrapper">

                    <div className="tournamentStatsWrapper " >
                        <StatsHeader
                            battingKPI={statsData.battingKPI}
                            battingStats={statsData.battingStats}
                            bowlingKPI={statsData.bowlingKPI}
                        />

                        <div id="mcBestPerformers">
                            <div className="mcContainer">
                                <h4 className="mcTitle">Top Performers</h4>

                                <PerformerCard
                                    title="Top Run Getter"
                                    performers={performers.maxRuns}
                                    id="topRunGetter"
                                    unit="RUNS"
                                />
                                <PerformerCard
                                    title="Leading Wicket Takers"
                                    performers={performers.wickets}
                                    id="leadWcktTakers"
                                    unit="WICKETS"
                                />
                                <PerformerCard
                                    title="Highest SR (Min: 25 Runs)"
                                    performers={performers.strikeRate}
                                    id="higiestSr"
                                    unit="SR"
                                />
                                <PerformerCard
                                    title="Best Economy (Min: 4 Overs)"
                                    performers={performers.economy}
                                    id="bestEcon"
                                    unit="ECON"
                                />
                            </div>
                        </div>

                        <div className="statsContent" >
                            <div className="mcContainer">
                                <div className="col s12 m12 l12">
                                    <ul className="mcTabs roleTab tourTab">
                                        <li
                                            className={`mcTabLink ${activeTab === 'batting' ? 'current' : ''}`}
                                            onClick={() => handleTabClick('batting')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <span>Batting Stats </span>
                                        </li>
                                        <li
                                            className={`mcTabLink ${activeTab === 'bowling' ? 'current' : ''}`}
                                            onClick={() => handleTabClick('bowling')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <span>Bowling Stats </span>
                                        </li>
                                    </ul>
                                    <span className="statsPrintIcon "></span>
                                    <span className="statsExportExcelIcon " ></span>
                                    <div style={{ display: activeTab === 'batting' ? 'block' : 'none' }}>
                                        <BattingStats
                                            competitionId={competitionId}
                                            statsFeed={statsFeed}
                                            onError={handleError}
                                        />
                                    </div>
                                    <div style={{ display: activeTab === 'bowling' ? 'block' : 'none' }}>
                                        <BowlingStats
                                            competitionId={competitionId}
                                            statsFeed={statsFeed}
                                            onError={handleError}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div >
            )}
            {activeMenu === 'team' && (
                <TeamStats
                    competitionId={competitionId}
                    statsFeed={statsFeed}
                    onError={handleError}
                />
            )}
            {activeMenu === 'player' && (
                <PlayerStats
                    competitionId={competitionId}
                    statsFeed={statsFeed}
                    onError={handleError}
                />
            )}
        </>
    );
}

export default Stats;